/* eslint semi: 1 */
import { defineStore } from 'pinia';
import _ from 'lodash';
import { proxyException } from '@/utils/communication';
import { COMPONENT_KEY, POP_IDS_STATE_KEY } from './storeUtils.js';
import { Dependencies } from '@/globals/utils.js';

export const popIdsStore = (storeId) => {
  return defineStore(`${storeId}-popIds-store`, {
    state: () => ({
      [POP_IDS_STATE_KEY]: [],
      error: null,
      isFetching: false
    }),

    actions: ({
      addPopIds (popIds) {
        if (!_.isArray(popIds)) {
          return;
        }
        /**
         * Format each popId as a state item and filter to ensure it is not already present in the current list.
         */
        const newPopIdsToAdd = _.chain(popIds)
          .filter(formattedPopId => !_.some(this[POP_IDS_STATE_KEY], { name: formattedPopId.name }))
          .value();

        if (!_.isEmpty(newPopIdsToAdd)) {
          this[POP_IDS_STATE_KEY].push(...newPopIdsToAdd);
        }
      },
      updatePopIdValue (popId, newValue) {
        this[POP_IDS_STATE_KEY] = this[POP_IDS_STATE_KEY].map(pi => {
          if (pi.name === popId.name) {
            pi = { ...pi, name: newValue, label: newValue, sortValue: newValue, value: newValue };
          }
          return pi;
        });
      },
      removePopIds (popIds) {
        if (!_.isArray(popIds)) {
          return;
        }

        this[POP_IDS_STATE_KEY] = _.differenceBy(this[POP_IDS_STATE_KEY], popIds, 'uid');
      },

      /**
       * Pull state of Pop IDs from the server
       */
      async fetchPopIds () {
        const $api = Dependencies.inject('$api');
        this.$patch(state => {
          state.error = null;
          state.isFetching = true;
        });

        const params = {};
        if (storeId === COMPONENT_KEY.LED_SCREEN || storeId === COMPONENT_KEY.VIDEOWALL_SCREEN) {
          params.contextFilter = 'led';
        }

        const { ok: response, error } = await proxyException(() => $api.get(`${CONFIG.API_BASE_URL}/media/popid`, { params }));

        this.clearPopIds();
        if (error) {
          const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to fetch user tags';
          this.$patch(state => {
            state.error = errorMessage;
            state.isFetching = false;
          });
          return;
        }

        this.addPopIds(response?.data?.popIds ?? []);
        this.isFetching = false;
      },
      async updatePopId (oldPopIdValue, newPopIdValue) {
        const $api = Dependencies.inject('$api');
        const popIdToUpdate = this[POP_IDS_STATE_KEY].find((popId) => popId.name === oldPopIdValue);

        if (!popIdToUpdate) {
          this.error = 'Cannot update this pop id. Pop id does not exist';

          return;
        }

        const { error } = await proxyException(() => $api
          .post(
            `${CONFIG.API_BASE_URL}/media/popid/${encodeURIComponent(oldPopIdValue)}/rename`,
            { newPopId: newPopIdValue }
          )
        );

        if (error) {
          const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to update pop id';
          this.$patch(state => {
            state.error = errorMessage;
            state.isFetching = false;
          });
          return;
        }

        this.updatePopIdValue(popIdToUpdate, newPopIdValue);
      },
      async deletePopId (popId) {
        const $api = Dependencies.inject('$api');
        const { error } = await proxyException(() => $api
          .delete(
            `${CONFIG.API_BASE_URL}/media/popid/${encodeURIComponent(popId.value)}`
          )
        );

        if (error) {
          const errorMessage = error.response ? error.response?.data.message ?? error.message : 'Failed to delete pop id';
          this.$patch(state => {
            state.error = errorMessage;
            state.isFetching = false;
          });

          return errorMessage;
        }

        this.removePopIds([popId]);
      },
      clearPopIds () {
        this[POP_IDS_STATE_KEY] = [];
      },
      clearState () {
        this.$patch((state) => {
          state[POP_IDS_STATE_KEY] = [];
          state.error = null;
          state.isFetching = false;
        });
      }
    })
  });
};
